import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { formatDate, formatFee } from '../../utils/format';
import { BOOKING_STATUS_LABELS, BOOKING_STATUS_COLORS } from '../../utils/bookingStatus';
import { useToast } from '../../contexts/ToastContext';
import BookerProfileForm from '../../components/BookerProfileForm';
import DashboardSkeleton from '../../components/DashboardSkeleton';

export default function BookerDashboard() {
  const { addToast } = useToast();
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [artists, setArtists] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  // Invite form
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState('');

  // Accept booking form
  const [acceptingBookingId, setAcceptingBookingId] = useState(null);
  const [acceptFormData, setAcceptFormData] = useState({});

  useEffect(() => {
    async function fetchData() {
      try {
        const [profileRes, artistsRes, invitationsRes, bookingsRes] = await Promise.allSettled([
          api.get('/bookers/profile/me'),
          api.get('/bookers/artists'),
          api.get('/bookers/artists/invitations'),
          api.get('/bookings/booker/mine'),
        ]);
        if (profileRes.status === 'fulfilled') setProfile(profileRes.value.data.profile || profileRes.value.data);
        if (artistsRes.status === 'fulfilled') setArtists(artistsRes.value.data.artists || []);
        if (invitationsRes.status === 'fulfilled') setInvitations(invitationsRes.value.data.invitations || []);
        if (bookingsRes.status === 'fulfilled') setBookings(bookingsRes.value.data.bookings || []);
      } catch {
        // Erreurs gérées individuellement
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  async function handleProfileCreated() {
    try {
      const res = await api.get('/bookers/profile/me');
      setProfile(res.data.profile || res.data);
      setEditing(false);
      addToast('Profil créé avec succès');
    } catch (err) {
      addToast(err.message, 'error');
    }
  }

  async function handleProfileUpdated() {
    try {
      const res = await api.get('/bookers/profile/me');
      setProfile(res.data.profile || res.data);
      setEditing(false);
      addToast('Profil mis à jour');
    } catch (err) {
      addToast(err.message, 'error');
    }
  }

  async function handleSearchArtists() {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await api.get(`/artists?search=${encodeURIComponent(searchQuery)}`);
      setSearchResults(res.data.artists || []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }

  async function handleInviteArtist(artistId) {
    setInviting(true);
    setInviteError('');
    try {
      await api.post('/bookers/artists/invite', { artistId });
      const res = await api.get('/bookers/artists/invitations');
      setInvitations(res.data.invitations || []);
      setSearchQuery('');
      setSearchResults([]);
      addToast('Invitation envoyée');
    } catch (err) {
      setInviteError(err.message || 'Erreur lors de l\'invitation');
    } finally {
      setInviting(false);
    }
  }

  async function handleRemoveArtist(artistId) {
    if (!confirm('Retirer cet artiste de votre roster ?')) return;
    try {
      await api.delete(`/bookers/artists/${artistId}`);
      setArtists((prev) => prev.filter((a) => a.id !== artistId));
      addToast('Artiste retiré du roster');
    } catch (err) {
      addToast(err.message, 'error');
    }
  }

  async function handleBookingAction(bookingId, action, acceptedFee = null) {
    try {
      const payload = {};
      if (action === 'accept' && acceptedFee !== null) {
        payload.acceptedFee = Math.round(parseFloat(acceptedFee) * 100);
      }
      const res = await api.patch(`/bookings/${bookingId}/${action}`, payload);
      // Statut réel renvoyé par l'API : 'accepted' ou 'counter_offer' (cachet différent)
      const updated = res.data.booking;
      setBookings((prev) =>
        prev.map((b) => (b.id === bookingId ? { ...b, ...updated } : b))
      );
      setAcceptingBookingId(null);
      setAcceptFormData({});
      addToast(
        action === 'reject'
          ? 'Booking refusé'
          : updated.status === 'counter_offer'
            ? 'Contre-proposition envoyée'
            : 'Booking accepté'
      );
    } catch (err) {
      addToast(err.message, 'error');
    }
  }

  if (loading) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 animate-fadeIn">
      <h1 className="th-page-title mb-8">Dashboard Bookeur</h1>

      {/* Mon Profil */}
      <section className="border th-border p-6 mb-8 th-bg-surface">
        <h2 className="th-section-title mb-4">Mon Profil</h2>
        {!profile ? (
          <BookerProfileForm mode="create" onSuccess={handleProfileCreated} />
        ) : editing ? (
          <BookerProfileForm mode="edit" initialData={profile} onSuccess={handleProfileUpdated} />
        ) : (
          <div className="flex items-start justify-between">
            <div>
              <p className="font-mono font-medium th-text text-sm">{profile.companyName || 'Profil Bookeur'}</p>
              <p className="text-xs th-text-muted font-mono mt-2">
                {profile.city && profile.country ? `${profile.city}, ${profile.country}` : 'Localisation non définie'}
              </p>
              {profile.bio && <p className="text-xs th-text-secondary mt-3">{profile.bio}</p>}
              {profile.phone && (
                <p className="text-xs th-text-muted font-mono mt-2">Tél : {profile.phone}</p>
              )}
              {profile.website && (
                <a
                  href={profile.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-accent hover:opacity-70 font-mono mt-1 inline-block transition-opacity duration-150"
                >
                  {profile.website}
                </a>
              )}
            </div>
            <button
              onClick={() => setEditing(true)}
              className="th-btn"
            >
              Modifier
            </button>
          </div>
        )}
      </section>

      {/* Mes Artistes */}
      <section className="mb-8">
        <h2 className="th-section-title mb-4">Mes Artistes</h2>

        {/* Recherche + Invitation */}
        <div className="border th-border p-4 mb-4 th-bg-surface">
          <div className="flex gap-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearchArtists()}
              placeholder="Rechercher un artiste..."
              className="th-input flex-1"
            />
            <button
              onClick={handleSearchArtists}
              disabled={searching}
              className="th-btn-accent disabled:opacity-50"
            >
              {searching ? '...' : 'Rechercher'}
            </button>
          </div>
          {inviteError && <p className="text-status-rejected text-xs mt-2 font-mono">{inviteError}</p>}
          {searchResults.length > 0 && (
            <div className="mt-3 space-y-px dark:bg-dark-border bg-light-border">
              {searchResults.map((artist) => (
                <div key={artist.id} className="th-bg p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <p className="font-mono font-medium th-text text-sm">{artist.artistName}</p>
                    <p className="text-xs th-text-muted font-mono">{artist.city || 'Pas de localisation'}</p>
                  </div>
                  <button
                    onClick={() => handleInviteArtist(artist.id)}
                    disabled={inviting}
                    className="th-btn-accent disabled:opacity-50 active:opacity-70 transition-opacity duration-100"
                  >
                    Inviter
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {artists.length === 0 ? (
          <div className="border th-border p-6 text-center th-text-secondary text-sm th-bg-surface">
            Aucun artiste dans votre roster. Recherchez un artiste pour l'inviter.
          </div>
        ) : (
          <div className="space-y-px dark:bg-dark-border bg-light-border">
            {artists.map((artist) => (
              <div key={artist.id} className="th-bg p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <Link
                    to={`/artists/${artist.slug}`}
                    className="font-mono font-medium th-text text-sm hover:text-accent transition-colors duration-150"
                  >
                    {artist.artistName || artist.name}
                  </Link>
                  <p className="text-xs th-text-muted font-mono mt-0.5">{artist.city || 'Pas de localisation'}</p>
                </div>
                <button
                  onClick={() => handleRemoveArtist(artist.id)}
                  className="border border-status-rejected/30 text-status-rejected text-xs font-mono uppercase tracking-widest px-3 py-1.5 hover:bg-status-rejected/10 transition-colors duration-150 active:opacity-70"
                >
                  Retirer
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Invitations Envoyées */}
      {invitations.length > 0 && (
        <section className="mb-8">
          <h2 className="th-section-title mb-4">Invitations Envoyées</h2>
          <div className="space-y-px dark:bg-dark-border bg-light-border">
            {invitations.map((inv) => (
              <div key={inv.id} className="th-bg p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="font-mono font-medium th-text text-sm">{inv.artistName || 'Artiste'}</p>
                  <p className="text-xs th-text-muted font-mono mt-0.5">{formatDate(inv.createdAt)}</p>
                </div>
                <span className="th-badge border-status-pending text-status-pending bg-status-pending/10">
                  En attente
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Bookings de mes Artistes */}
      <section>
        <h2 className="th-section-title mb-4">Bookings de mes Artistes</h2>
        {bookings.length === 0 ? (
          <div className="border th-border p-6 text-center th-text-secondary text-sm th-bg-surface">
            Aucun booking pour vos artistes.
          </div>
        ) : (
          <div className="space-y-px dark:bg-dark-border bg-light-border">
            {bookings.map((booking) => (
              <div key={booking.id}>
                {acceptingBookingId === booking.id ? (
                  <div className="th-bg p-5 space-y-4">
                    <div>
                      <p className="font-mono font-medium th-text text-sm mb-3">
                        {booking.artistName || 'Artiste'} · {booking.eventTitle || 'Événement'}
                      </p>
                      {booking.proposedFee && (
                        <p className="text-xs th-text-secondary font-mono mb-4">
                          Cachet proposé : {formatFee(booking.proposedFee, booking.currency)}
                        </p>
                      )}
                      <label className="block text-xs font-mono th-text-secondary mb-2 uppercase tracking-widest">
                        Cachet accepté (optionnel)
                      </label>
                      <input
                        type="number"
                        value={acceptFormData.acceptedFee || ''}
                        onChange={(e) => setAcceptFormData({ acceptedFee: e.target.value })}
                        min="0"
                        step="100"
                        placeholder="Laisser vide pour accepter le montant proposé"
                        className="th-input w-full mb-4"
                      />
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleBookingAction(booking.id, 'accept', acceptFormData.acceptedFee || null)}
                        className="th-btn-accent flex-1"
                      >
                        Accepter
                      </button>
                      <button
                        onClick={() => {
                          setAcceptingBookingId(null);
                          setAcceptFormData({});
                        }}
                        className="th-btn flex-1"
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="th-bg p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-mono font-medium th-text text-sm">
                          {booking.artistName || 'Artiste'} · {booking.eventTitle || 'Événement'}
                        </p>
                        <Link
                          to={`/bookings/${booking.id}?tab=messages`}
                          className="text-[10px] font-mono text-accent hover:opacity-70 transition-opacity duration-150"
                        >
                          Messages →
                        </Link>
                      </div>
                      <p className="text-xs th-text-muted font-mono mt-0.5">
                        {booking.promoterCompanyName || 'Promoteur'} · {formatDate(booking.eventDate || booking.createdAt)}
                      </p>
                      {booking.proposedFee && (
                        <p className="text-xs th-text-secondary font-mono mt-1">
                          Cachet proposé : {formatFee(booking.proposedFee, booking.currency)}
                        </p>
                      )}
                      {booking.status === 'counter_offer' && booking.acceptedFee != null && (
                        <p className="text-xs text-status-pending font-mono mt-1">
                          Contre-proposition envoyée : {formatFee(booking.acceptedFee, booking.currency)} (en attente du promoteur)
                        </p>
                      )}
                      {booking.travelIncluded && (
                        <p className="text-xs th-text-secondary font-mono mt-1">✓ Voyage inclus</p>
                      )}
                      {booking.accommodationIncluded && (
                        <p className="text-xs th-text-secondary font-mono">✓ Hébergement inclus</p>
                      )}
                      {booking.technicalRequirements && (
                        <div className="mt-2 p-2 th-bg-elevated border th-border">
                          <p className="text-[10px] font-mono th-text-muted uppercase tracking-widest mb-1">Rider</p>
                          <p className="text-xs th-text-secondary whitespace-pre-wrap">{booking.technicalRequirements}</p>
                        </div>
                      )}
                      {booking.message && (
                        <p className="text-xs th-text-secondary mt-1 italic font-sans">"{booking.message}"</p>
                      )}
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className={`th-badge ${BOOKING_STATUS_COLORS[booking.status]}`}>
                        {BOOKING_STATUS_LABELS[booking.status]}
                      </span>
                      {booking.status === 'pending' && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setAcceptingBookingId(booking.id);
                              setAcceptFormData({});
                            }}
                            className="th-btn-accent active:opacity-70 transition-opacity duration-100"
                          >
                            Accepter
                          </button>
                          <button
                            onClick={() => {
                              if (confirm('Êtes-vous sûr de vouloir refuser ce booking ? Cette action est irréversible.')) {
                                handleBookingAction(booking.id, 'reject');
                              }
                            }}
                            className="border border-status-rejected/30 text-status-rejected text-xs font-mono uppercase tracking-widest px-3 py-1.5 hover:bg-status-rejected/10 transition-colors duration-150 active:opacity-70"
                          >
                            Refuser
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
